import { ITreeNodeData } from "../../../treeview/types/TreeNodeData";

export const applyInitialExpansionState = <T extends { sourceID?: number; fakeID?: number }>(
    oldTree: ITreeNodeData<T> | null, 
    newTree: ITreeNodeData<T> | null, 
    forceExpandedId?: number
): ITreeNodeData<T> | null => {
    if (!newTree) return newTree;

    const expandedNodeIds = new Set<number>();

    const collectExpandedNodes = (node: ITreeNodeData<T> | null): void => {
        if (!node) return;

        const nodeKey = getNodeKey(node);

        if (node.isExpanded) {
            expandedNodeIds.add(nodeKey);
        }

        node.children?.forEach(collectExpandedNodes);
    };

    const applyExpandedNodes = (node: ITreeNodeData<T> | null): ITreeNodeData<T> | null => {
        if (!node) return node;

        const nodeKey = getNodeKey(node);

        return {
            ...node,
            isExpanded:
                nodeKey === forceExpandedId || node.level === 0
                    ? true
                    : expandedNodeIds.has(nodeKey),
            children: node.children?.map(applyExpandedNodes) || []
        };
    };

    collectExpandedNodes(oldTree);

    return applyExpandedNodes(newTree);
};

export const updateTreeNodeState = <T>(
    node: ITreeNodeData<T> | null,
    targetId: number,
    selectedId?: number
): ITreeNodeData<T> | null => {
    if (!node) return node;

    const hasChildren = !!(node.children && node.children.length > 0);
    const isTarget = node.id === targetId;
    const isPreviouslySelected = selectedId !== undefined && node.id === selectedId;

    const updatedNode: ITreeNodeData<T> = {
        ...node,
        active: isTarget,
        children: node.children?.map(child =>
            updateTreeNodeState(child, targetId, selectedId)
        ) || []
    };

    if (isTarget && hasChildren) {
        const isSameSelectedNode = selectedId === targetId;
        updatedNode.isExpanded = isSameSelectedNode
            ? !node.isExpanded
            : true;
    }

    if (!isTarget && isPreviouslySelected) {
        updatedNode.active = false;
    }

    return updatedNode;
};


const getNodeKey = <T extends { sourceID?: number; fakeID?: number }>(
    node: ITreeNodeData<T>
): number => {
    return node.id > 0 ? node.id : node.object?.fakeID;
};